/**
 * The Visits board's Needs attention cell (WBS-12793): the reason as a pill in its tone, so it stands out among
 * the column's empty cells, and nothing at all when the Visit needs no one. The board's generic value cell prints
 * the bare enum label and "Not Set"; this draws `visitNeedsAttentionTag` instead, so the table, the card and the
 * header share one label, tone and hover detail.
 */
import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation } from '@angular/core';
import { MetadataEntityFieldData, VisitBrowseRow } from '@jms/model';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { BadgeComponent } from '@workbuddy/ui/v2/components/badge';
import type { TagDeriverStyle } from '@workbuddy/ui/v2/surface.engine';
import { WeekdayDateCellRenderer } from '../../shared/weekday.date.cell.renderer';
import { visitNeedsAttentionTag } from './visit.needs.attention.tag';

/** Mounted by the Visit manifest's `table.overrideCellRenderer` on the `needsAttention` column. */
@Component({
  selector: 'visit-needs-attention-cell-renderer',
  standalone: true,
  imports: [BadgeComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: flex; align-items: center; min-width: 0; height: 100%; overflow: hidden;' },
  template: `
    @if ($pill(); as pill) {
      <wb-badge size="small" [label]="pill.label" [color]="pill.color" [backgroundColor]="pill.backgroundColor" [borderColor]="pill.borderColor" [tooltip]="pill.tooltip ?? pill.label" [onlyShowTooltipIfOverflow]="!pill.tooltip"></wb-badge>
    }
  `,
})
export class VisitNeedsAttentionCellRenderer implements ICellRendererAngularComp {

  protected readonly $pill = signal<TagDeriverStyle | null>(null);

  public agInit(params: ICellRendererParams<VisitBrowseRow>): void {

    this.$pill.set(visitNeedsAttentionTag(params.value, params.data));
  }

  public refresh(params: ICellRendererParams<VisitBrowseRow>): boolean {

    this.agInit(params);
    return true;
  }
}

/**
 * The Visit boards' `table.overrideCellRenderer`: swaps the plain enum text on `needsAttention` for the pill, and
 * names the weekday on `startDate` (WBS-12795) in the Visit's own zone.
 */
export function visitCellRenderer(_context: unknown, _metadata: unknown, field: MetadataEntityFieldData, renderer: Record<string, unknown>): Record<string, unknown> {

  switch (field.name) {
    case 'needsAttention': return { ...renderer, cellRenderer: VisitNeedsAttentionCellRenderer };
    case 'startDate': return { ...renderer, cellRenderer: WeekdayDateCellRenderer };
    default: return renderer;
  }
}
